import _ from 'lodash'
import {fetch, post, put, remove} from '@/base-http';
import AppRelease from '@/modules/app-router/model/AppRelease';
import App from '@/modules/app-router/model/App';
import KeyValuePair from '@/modules/core/support/KeyValuePair';
import Api from '@/modules/app-router/model/Api';
import ApiVersion from '@/modules/app-router/model/ApiVersion';
import GroupReleaseAssignation from '@/modules/app-router/model/GroupReleaseAssignation'
import Log from '@/modules/app-router/model/ReleaseLog'
import ReleaseLog from '@/modules/app-router/model/ReleaseLog'
import moment from 'moment';
import User from '@/modules/user/model/User';
import Company from '@/modules/app-router/model/Company';
import UnigestUser from '@/modules/app-router/model/UnigestUser';
import SQLConnectionConfig from '@/modules/app-router/model/SQLConnectionConfig';
import RegisterPlatformUserData from '../model/RegisterPlatformUser';
import UserPlaform from '@/modules/app-router/model/UserPlaform';
import UserPlaformStatusChange from '../model/UserPlaformStatusChange';
export interface DuplicateOptions {
    version: string
    apiVersion?: number
}

export interface FastTokenOptions {
    serial: string
    company: string
    user: string

    /**
     * ID de la versión de la aplicación que será asignada
     */
    release: number
}

/**
 * Token rápido generado por el servidor
 */
export class FastToken {
    public token = ''
    /**
     * Identificador de la sesión del enrutador de aplicaciones generada para el token
     */
    public session = ''
}

/**
 * Parámetros que pueden ser enviados al método getReleaseLogs para
 * limitar o filtrar la lista
 */
export interface GetReleaseLogInput {
    releases?: AppRelease[]
    user?: User
    limit?: number
    since?: string
    levels?: string[]
    sinceDate?: Date
    untilDate?: Date
    sortBy?: string[]
    sortDesc?: boolean[]
}

export interface ReleaseLogsList {
    records: Log[]
    totalCount: number
}

/**
 * Permite obtener a través de la API la lista de aplicaciones registradas en el
 * enrutador
 */
export async function listApps () {
    const data = await fetch('/app-router/apps')
    if (!Array.isArray(data)) {
        throw new Error(`Se esperaba un array con la lista de aplicaciones, se obtuvo ${typeof data}`)
    }

    return data.map(app => App.deserialize(app))
}
/**
 * Permite obtener la lista de distribuciones de la aplicación registradas
 *
 * @return { Promise<AppRelease[]> }
 */
export async function listAppReleases () {
    const data = await fetch('/app-router/releases')
    if (!Array.isArray(data)) {
        throw new Error(`Se esperaba un array con la lista de distribuciones, se obtuvo ${typeof data}`)
    }

    return data.map(release => AppRelease.deserialize(release))
}

/**
 * Registra una nueva publicación de la aplicación
 *
 * @param app Identificador de la app
 * @param release
 */
export async function createAppRelease (app: number, release: AppRelease) {
    if (!release.isNew) {
        throw new Error('No se puede inserta una publicación que ya sido registrada')
    }

    // El archivo es obligatorio
    if (!release.distFile) {
        throw new Error('La publicación no incluye el archivo de distribución')
    }

    let data = new FormData()
    data.append('data', JSON.stringify(release.serialize()))
    data.append('file', release.distFile)

    try {
        data = await post(`/app-router/apps/${app}/release`, data, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
    } catch (e) {
        throw new Error(_.get(e, 'response.data.message', e.message))
    }

    return AppRelease.deserialize(data)
}

/**
 * Actualiza la información de una publicación
 *
 * @param release
 */
export async function updateAppRelease (release: AppRelease) {
    if (release.isNew) {
        throw new Error('No se puede actualizar una publicación que aún no ha sido registrada')
    }

    const data = await put(`app-router/releases/${release.id}`, release.serialize())

    return AppRelease.deserialize(data)
}

/**
 * Solicita al servidor la lista de APIs registradas en la aplicación
 */
export async function listApis () {
    const data = await fetch('/app-router/apis')
    if (!Array.isArray(data)) {
        throw new Error(`Se esperaba una lista con las apis registradas, se obtuvo ${typeof data}`)
    }

    return data.map((a: KeyValuePair) => Api.deserialize(a))
}

/**
 * Actualiza la información de la versión de la API argumentada
 *
 * @param version
 */
export async function updateApiVersion (version: ApiVersion) {
    if (version.isNew) {
        throw new Error('No se puede actualizar un registro que aún no ha sido registrado')
    }

    const data = await put(`/app-router/api-versions/${version.id}`, version.serialize())

    return ApiVersion.deserialize(data)
}

/**
 * Actualiza la información de la versión de la API argumentada
 *
 * @param api Id de la API donde se registrará una versión
 * @param version
 */
export async function createApiVersion (api: number, version: ApiVersion) {
    if (!version.isNew) {
        throw new Error('No se puede actualizar un registro que aún no ha sido registrado')
    }

    try {
        const data = await post(`/app-router/apis/${api}/version`, version.serialize())

        return ApiVersion.deserialize(data)
    } catch (e) {
        throw new Error(_.get(e, 'response.data.message', e.message))
    }
}

/**
 * Solicita a la API la eliminación de la versión de API asociada al id
 * argumentada
 *
 * @param id
 */
export async function deleteApiVersion (id: number) {
    await remove(`/app-router/api-versions/${id}`)
}

/**
 * Elimina una distribución
 *
 * @param id
 */
export async function deleteAppRelease (id: number) {
    await remove(`/app-router/releases/${id}`)
}

/**
 * Solicita a la API la lista de asignaciones de distribuciones a grupos
 * de usuarios.
 */
export async function listGroupReleaseAssignations () {
    const data = await fetch('/app-router/group-release-assignations')
    if (!Array.isArray(data)) {
        throw new Error('Se esperaba un array con la lista de asignaciones')
    }

    return data.map((a: KeyValuePair) => GroupReleaseAssignation.deserialize(a))
}

/**
 * Actualiza la lista de asignaciones de distribuciones a grupos
 * de usuarios
 */
export async function updateGroupReleaseAssignations (assignations: GroupReleaseAssignation[]) {
   try {
       const result = await put(
           '/app-router/group-release-assignations',
           assignations.map(a => a.serialize()),
       )

       if (!Array.isArray(result)) {
           throw new Error('Se esperaba un array con la lista de asignaciones')
       }

       return result.map((a: KeyValuePair) => GroupReleaseAssignation.deserialize(a))
   } catch (e) {
       throw new Error(_.get(e, 'response.data.message', e.message))
   }
}

/**
 * Duplica una distribución
 */
export async function duplicateRelease (id: number, options: DuplicateOptions) {
    const result = await post(`app-router/releases/${id}/copy`, {
        version: options.version,
        api: options.apiVersion,
    })

    return AppRelease.deserialize(result)
}

/**
 * Solicita a la API la lista de registros de eventos generados
 * por la distribución argumentada
 *
 * @param input Filtros de búsqueda
 */
export async function getReleaseLogs (input: GetReleaseLogInput) {
    const params: KeyValuePair = {}

    if (input.releases && input.releases.length) {
        params.releases = input.releases.map(r => r.id).join(',')
    }

    // Los criterios de búsqueda incluyen ún
    if (input.user) {
        params.user = input.user.email
    }

    if (input.since) {
        params.since = input.since
    }

    if (input.limit) {
        params.limit = input.limit
    }

    if (input.levels) {
        params.levels = input.levels.join(',')
    }

    if (input.sinceDate) {
        params.sinceDate = moment(input.sinceDate).format('DD-MM-YYYY HH:mm:ss')
    }

    if (input.untilDate) {
        params.untilDate = moment(input.untilDate).format('DD-MM-YYYY HH:mm:ss')
    }

    if (input.sortBy && input.sortBy.length) {
        params.sortBy = input.sortBy.join(',')
    }

    if (input.sortDesc && input.sortDesc.length) {
        params.sortDesc = input.sortDesc.join(',')
    }

    const result = await fetch(`/app-router/logs`, {
        params,
    })

    const list: ReleaseLogsList = {
        records: result.records.map((record: any) => ReleaseLog.deserialize(record)),
        totalCount: result.totalCount || 0,
    }

    return list
}

/**
 * Solicita a la API la información del evento asociado al id
 * argumentado
 *
 * @param id { string }
 */
export async function getLog (id: string) {
    const data = await fetch(`/app-router/logs/${id}`)

    return ReleaseLog.deserialize(data)
}

/**
 * Obtiene la lista de empresas asociadas a un número de serie
 * @param serial
 * @param sqlConnection Nombre de la conexión SQL utilizada
 */
export async function getCompanyList (serial: string, sqlConnection = '') {
    const params = new URLSearchParams()
    if (sqlConnection) {
        params.append('sql_connection', sqlConnection)
    }

    const result = await fetch(`/app-router/unigest/installation/${serial}/companies`, {params})
    if (!Array.isArray(result)) {
        throw new Error(`Se esperaba una lista de empresas pero se obtuvo el tipo ${typeof result}`)
    }

    return result.map(c => Company.deserialize(c))
}

/**
 * Obtiene la lista de usuarios registrado a un determinado número de serie
 * @param serial
 * @param sqlConnection Conexión SQL utilizada
 */
export async function getUnigestUserList (serial: string, sqlConnection = '') {
    const params = new URLSearchParams()
    if (sqlConnection) {
        params.append('sql_connection', sqlConnection)
    }

    const result = await fetch(`/app-router/unigest/installation/${serial}/users`, {params})
    if (!Array.isArray(result)) {
        throw new Error(`Se esperaba una lista de usuarios pero se obtuvo el tipo ${typeof result}`)
    }

    return result.map(u => UnigestUser.deserialize(u))
}

/**
 * Permite obtener un token rápido de Unigest
 *
 * @param options
 */
export async function getUnigestFastToken (options: FastTokenOptions) {
    const data = await post('/app-router/auth/fast-token', {
        user: options.user,
        serial: options.serial,
        company: options.company,
        release: options.release,
    })

    return Object.assign(new FastToken(), {
        token: data.token,
        session: data.session,
    })
}

/**
 * Retorna la lista de conexiones configuradas en la UTSL
 */
export async function getSQLConnections () {
    const data = await fetch('/app-router/unigest/sql-connections')
    if (!Array.isArray(data)) {
        throw new Error('No se ha podido deserializar la lista de conexiones SQL')
    }

    return data.map(c => Object.assign(new SQLConnectionConfig(), c))
}

/**
 * Registra un usuario en la plataforma Unigest.net
 * (idUser: number, password: string, role: number, isCreateCompany: boolean, nameCompany: string, apiUrl: string)
 */
export async function registerPlatformUser (data: RegisterPlatformUserData) {
    const paramt =  {
        id: data.idUser,
        password: data.password,
        role: data.roleType,
        isCreateCompany: data.isCreateCompany,
        idEmpresa: data.idEmpresa,
        nameCompany: data.nameCompany,
        apiUrl: data.apiURL,
        isBlockedUser: data.isBlockedUser,
        isVerifyEmail: data.isVerifyEmail,
    }
    return await post('/app-router/unigest/platform/user', paramt)
}

/**
 * Retorna la lista de empresas (array string) que tiene un usuario creadas en Unigest.net, se obtien a través
 * de su nº de serie
 * (serialNumber: string)
 */
export async function getCompanyBySerialNumber (serialNumber: string, idUser: number ) {
    // export async function getCompanyBySerialNumber (serialNumber: string, api: number ) {
    const id = idUser
    const data = await fetch('/app-router/unigest/platform/companies/' + serialNumber, {params: { id }})

    if (!data || !data.companies || !Array.isArray(data.companies)) {
        throw new Error('Se esperaba una lista con los códigos de empresa')
    }
    return data // data.map(pc => PostalCode.deserialize(pc))
}

/**
 * Obtiene un usuario plataforma web de Unigest desde la API de artisan
 *
 * @param id
 */
export async function getPlatformUser (idUser: number, idApi: number) {
    try {
        const data = await fetch(`app-router/unigest/platform/user/${idUser}?api=${idApi}`)
        return UserPlaform.deserialize(data)
    } catch (e) {
        throw new Error(_.get(e, 'response.data.message', e.message))
    }
}

/**
 * Cambia el estado de un usuario plataforma web de Unigest desde la API de artisan
 * Bloquea o borra un uario.
 * @param id
 */
export async function ChangeUserStatusPlatform(data: UserPlaformStatusChange) {
    return await post('/app-router/unigest/platform/change', {
        id: data.id,
        serialOrEmail: data.serialOrEmail,
        scopeAffected: data.scopeAffected,
        userStatus: data.userStatus,
        borradoTotal: data.borradoTotal,
        hacerCopiaBD: data.hacerCopiaBD,
        emailConfirmed: data.emailConfirmed,
        apiUrl: data.apiUrl,
    })
}
